import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, IconButton, Tooltip, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem, Select, FormControl, InputLabel, Snackbar, Alert, Stack, ToggleButton, ToggleButtonGroup, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Chat as ChatIcon, Search as SearchIcon,
  Archive as ArchiveIcon, DirectionsCar as CarIcon, AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon, HourglassEmpty as WaitingIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import {
  ZONE_COLUMNS, ZONE_COLORS, INTEREST_COLORS, STAGE_LABELS,
  bucketZone, timeAgo, avisoVentana, leadName,
} from './leadsShared';

/**
 * Tablero de Leads
 *
 * Vista de trabajo sobre lo que dejó el clasificador diario
 * (backend/cron/leadClassifier.js): zona, municipio, etapa, interés, resumen de
 * la conversación y el seguimiento tentativo — todo sacado por IA leyendo el
 * chat completo.
 *
 * El objetivo es no tener que abrir conversación por conversación: se filtra
 * por zona/etapa/interés, se lee el resumen en la fila, y de ahí se salta
 * directo al chat de ese lead. La etapa se puede corregir desde la misma fila.
 */

const INTEREST_ORDER: Record<string, number> = { alto: 0, medio: 1, bajo: 2 };

type Orden = 'seguimiento' | 'interes' | 'reciente';

const LeadsBoardPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const esMovil = useMediaQuery(theme.breakpoints.down('md'));

  const [leads, setLeads] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState<{ msg: string; tipo: 'success' | 'error' } | null>(null);

  const [zona, setZona] = useState('');
  const [etapa, setEtapa] = useState('');
  const [interes, setInteres] = useState('');
  const [soloEsperan, setSoloEsperan] = useState(false);
  const [soloSeguimiento, setSoloSeguimiento] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  // Por fecha, de lo más reciente a lo más viejo: es el orden en que se trabaja el tablero.
  const [orden, setOrden] = useState<Orden>('reciente');
  /**
   * Hasta dónde mirar atrás. Por defecto 30 días: de las 1,020 conversaciones que existen,
   * 440 son de enero y febrero y lo único que hacían era tapar lo que está vivo. Quien
   * necesite rebuscar sube el rango; nada se borra, solo deja de estorbar.
   */
  const [dias, setDias] = useState(30);

  // Agendar seguimiento: a quién, cuándo y qué hay que decirle.
  const [agendando, setAgendando] = useState<any | null>(null);
  const [cuando, setCuando] = useState('');
  const [nota, setNota] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await apiService.getBotConversations({ limit: 300, dias });
      setLeads(res.data || res || []);
    } catch {
      setAviso({ msg: 'No se pudieron cargar los leads', tipo: 'error' });
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => { cargar(); }, [cargar]);

  // ---- Conteo por zona (los chips de arriba, que además filtran) ----
  const porZona = useMemo(() => {
    const m: Record<string, number> = {};
    for (const z of ZONE_COLUMNS) m[z] = 0;
    for (const l of leads) {
      const z = bucketZone(l);
      m[z] = (m[z] || 0) + 1;
    }
    return m;
  }, [leads]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const lista = leads.filter((l) => {
      if (zona && bucketZone(l) !== zona) return false;
      if (etapa && (l.flowStage || 'new') !== etapa) return false;
      if (interes && l.clasificacion?.interes !== interes) return false;
      if (soloEsperan && !(l.clasificacion?.esperaRespuesta || l.lastSender === 'client')) return false;
      if (soloSeguimiento && !l.pendingFollowUpAt) return false;
      if (q) {
        const heno = [
          l.leadName, l.contactName, l.phoneNumber, l.clientLocation,
          l.clasificacion?.resumen, l.clasificacion?.municipio, l.clasificacion?.tipoVehiculo,
        ].filter(Boolean).join(' ').toLowerCase();
        if (heno.indexOf(q) === -1) return false;
      }
      return true;
    });

    const tiempo = (v: any) => { const t = new Date(v).getTime(); return isNaN(t) ? 0 : t; };
    lista.sort((a, b) => {
      if (orden === 'interes') {
        const ia = INTEREST_ORDER[a.clasificacion?.interes] ?? 3;
        const ib = INTEREST_ORDER[b.clasificacion?.interes] ?? 3;
        if (ia !== ib) return ia - ib;
        return tiempo(b.lastMessageAt) - tiempo(a.lastMessageAt);
      }
      if (orden === 'reciente') return tiempo(b.lastMessageAt) - tiempo(a.lastMessageAt);
      // 'seguimiento': primero los que tienen fecha, la más próxima arriba
      const fa = a.pendingFollowUpAt ? tiempo(a.pendingFollowUpAt) : Infinity;
      const fb = b.pendingFollowUpAt ? tiempo(b.pendingFollowUpAt) : Infinity;
      if (fa !== fb) return fa - fb;
      return tiempo(b.lastMessageAt) - tiempo(a.lastMessageAt);
    });
    return lista;
  }, [leads, zona, etapa, interes, soloEsperan, soloSeguimiento, busqueda, orden]);

  const abrirChat = (phone: string) => navigate(`/bot/leads?phone=${encodeURIComponent(phone)}`);

  const cambiarEtapa = async (phone: string, nueva: string) => {
    const previo = leads;
    setLeads((prev) => prev.map((l) => (l.phoneNumber === phone ? { ...l, flowStage: nueva } : l)));
    try {
      await apiService.setFlowStage(phone, nueva);
    } catch {
      setLeads(previo);
      setAviso({ msg: 'No se pudo cambiar la etapa', tipo: 'error' });
    }
  };

  /**
   * Abre el diálogo con lo que ya hubiera agendado. Por defecto propone mañana a las 9, que
   * es la hora en que sale el recordatorio a Slack.
   */
  const abrirAgenda = (l: any) => {
    const base = l.pendingFollowUpAt ? new Date(l.pendingFollowUpAt) : null;
    const d = base && !isNaN(base.getTime()) ? base : (() => {
      const m = new Date();
      m.setDate(m.getDate() + 1);
      m.setHours(9, 0, 0, 0);
      return m;
    })();
    // `datetime-local` quiere la hora local sin zona, no un ISO en UTC.
    const pad = (n: number) => String(n).padStart(2, '0');
    setCuando(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setNota(l.pendingFollowUp || '');
    setAgendando(l);
  };

  const enDias = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    d.setHours(9, 0, 0, 0);
    const pad = (x: number) => String(x).padStart(2, '0');
    setCuando(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  };

  const guardarAgenda = async () => {
    if (!agendando) return;
    const phone = agendando.phoneNumber;
    const iso = cuando ? new Date(cuando).toISOString() : null;
    if (!iso) { setAviso({ msg: 'Pon una fecha', tipo: 'error' }); return; }
    const previo = leads;
    setLeads((prev) => prev.map((l) => (
      l.phoneNumber === phone ? { ...l, pendingFollowUpAt: iso, pendingFollowUp: nota } : l
    )));
    setAgendando(null);
    try {
      await apiService.bulkConversations([phone], 'followup', { followUp: nota || 'Dar seguimiento', followUpAt: iso });
      setAviso({ msg: 'Agendado. El recordatorio llega por Slack ese día.', tipo: 'success' });
    } catch {
      setLeads(previo);
      setAviso({ msg: 'No se pudo agendar', tipo: 'error' });
    }
  };

  const quitarAgenda = async () => {
    if (!agendando) return;
    const phone = agendando.phoneNumber;
    const previo = leads;
    setLeads((prev) => prev.map((l) => (
      l.phoneNumber === phone ? { ...l, pendingFollowUpAt: null, pendingFollowUp: null } : l
    )));
    setAgendando(null);
    try {
      await apiService.bulkConversations([phone], 'followup', {});
      setAviso({ msg: 'Seguimiento quitado', tipo: 'success' });
    } catch {
      setLeads(previo);
      setAviso({ msg: 'No se pudo quitar', tipo: 'error' });
    }
  };

  const sacarDelTablero = async (phone: string) => {
    const previo = leads;
    setLeads((prev) => prev.filter((l) => l.phoneNumber !== phone));
    try {
      await apiService.bulkConversations([phone], 'archive', { archived: true });
      setAviso({ msg: 'Lead sacado del tablero', tipo: 'success' });
    } catch {
      setLeads(previo);
      setAviso({ msg: 'No se pudo sacar del tablero', tipo: 'error' });
    }
  };

  // ---- Piezas de presentación ----

  const chipZona = (l: any) => {
    const z = bucketZone(l);
    const municipio = l.clientLocation || l.clasificacion?.municipio || '';
    return (
      <Box>
        <Chip label={z} size="small" sx={{ height: 20, fontSize: 11, bgcolor: ZONE_COLORS[z], color: '#fff' }} />
        {municipio && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
            {municipio}
          </Typography>
        )}
      </Box>
    );
  };

  const selectorEtapa = (l: any) => (
    <Select
      value={l.flowStage || 'new'}
      onChange={(e) => cambiarEtapa(l.phoneNumber, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      size="small"
      variant="standard"
      disableUnderline
      sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.25 } }}
    >
      {Object.keys(STAGE_LABELS).map((k) => (
        <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>{STAGE_LABELS[k].label}</MenuItem>
      ))}
    </Select>
  );

  const puntoInteres = (l: any) =>
    l.clasificacion?.interes ? (
      <Tooltip title={`Interés ${l.clasificacion.interes}`}>
        <Box sx={{
          width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
          bgcolor: INTEREST_COLORS[l.clasificacion.interes] || '#9e9e9e',
        }} />
      </Tooltip>
    ) : null;

  const seguimientoDe = (l: any) => {
    if (!l.pendingFollowUpAt) return null;
    const f = new Date(l.pendingFollowUpAt);
    const vencido = f.getTime() <= Date.now();
    return (
      <Box>
        <Chip
          icon={<ScheduleIcon sx={{ fontSize: 13 }} />}
          label={f.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' })}
          size="small"
          color={vencido ? 'error' : 'default'}
          sx={{ height: 20, fontSize: 11 }}
        />
        {l.pendingFollowUp && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.2 }}>
            {l.pendingFollowUp}
          </Typography>
        )}
      </Box>
    );
  };

  const esperando = (l: any) => l.clasificacion?.esperaRespuesta || l.lastSender === 'client';

  // ---- Móvil: tarjetas. La hermana trabaja desde el teléfono. ----
  const renderTarjeta = (l: any) => (
    <Paper
      key={l.phoneNumber}
      onClick={() => abrirChat(l.phoneNumber)}
      sx={{
        p: 1.5, mb: 1, cursor: 'pointer', borderLeft: '4px solid',
        borderColor: esperando(l) ? 'error.main' : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        {puntoInteres(l)}
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap>{leadName(l)}</Typography>
        <Chip
          label={STAGE_LABELS[l.flowStage || 'new']?.label || l.flowStage}
          size="small" color={STAGE_LABELS[l.flowStage || 'new']?.color}
          sx={{ height: 20, fontSize: 10 }}
        />
      </Box>
      {l.clasificacion?.resumen && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 0.75 }}>
          {l.clasificacion.resumen}
        </Typography>
      )}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
        {chipZona(l)}
        {l.moneyMentioned && <MoneyIcon sx={{ fontSize: 16, color: '#2e7d32' }} />}
        {l.leadVehicleCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <CarIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography variant="caption">{l.leadVehicleCount}</Typography>
          </Box>
        )}
        {esperando(l) && <Chip icon={<WaitingIcon sx={{ fontSize: 13 }} />} label="espera" size="small" color="error" sx={{ height: 20, fontSize: 10 }} />}
      </Stack>
      <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>{seguimientoDe(l)}</Box>
        <Typography variant="caption" color="text.secondary">{timeAgo(l.lastMessageAt)}</Typography>
        <Tooltip title="Agendar seguimiento">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); abrirAgenda(l); }}>
            <ScheduleIcon fontSize="small" color={l.pendingFollowUpAt ? 'secondary' : 'inherit'} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Descartar: sale del tablero, el chat no se borra">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); sacarDelTablero(l.phoneNumber); }}
          >
            <ArchiveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );

  const renderFila = (l: any) => (
    <TableRow
      key={l.phoneNumber}
      hover
      onClick={() => abrirChat(l.phoneNumber)}
      sx={{ cursor: 'pointer', '& td': { verticalAlign: 'top', py: 1 } }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {puntoInteres(l)}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{leadName(l)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {timeAgo(l.lastMessageAt)}{avisoVentana(l)}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>{chipZona(l)}</TableCell>
      <TableCell>{selectorEtapa(l)}</TableCell>
      <TableCell sx={{ maxWidth: 340 }}>
        <Typography variant="body2" sx={{ fontSize: 13 }}>
          {l.clasificacion?.resumen || <span style={{ opacity: 0.5 }}>Sin clasificar todavía</span>}
        </Typography>
        {l.clasificacion?.objecion && (
          <Typography variant="caption" color="warning.main" display="block">
            Freno: {l.clasificacion.objecion}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {l.moneyMentioned && <Tooltip title="Ya sabe el precio"><MoneyIcon sx={{ fontSize: 16, color: '#2e7d32' }} /></Tooltip>}
          {l.leadVehicleCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <CarIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="caption">{l.leadVehicleCount}</Typography>
            </Box>
          )}
          {esperando(l) && <Tooltip title="Espera respuesta"><WaitingIcon sx={{ fontSize: 16, color: 'error.main' }} /></Tooltip>}
        </Stack>
        {l.clasificacion?.tipoVehiculo && (
          <Typography variant="caption" color="text.secondary" display="block">{l.clasificacion.tipoVehiculo}</Typography>
        )}
      </TableCell>
      <TableCell>{seguimientoDe(l)}</TableCell>
      <TableCell align="right">
        <Tooltip title="Abrir el chat">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); abrirChat(l.phoneNumber); }}>
            <ChatIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Agendar seguimiento">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); abrirAgenda(l); }}>
            <ScheduleIcon fontSize="small" color={l.pendingFollowUpAt ? 'secondary' : 'inherit'} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Descartar: sale del tablero, el chat no se borra">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); sacarDelTablero(l.phoneNumber); }}>
            <ArchiveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" fontWeight={600}>Leads</Typography>
          <Typography variant="body2" color="text.secondary">
            Clasificados a diario por IA leyendo cada conversación. Toca un lead para abrir su chat.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={cargar} size="small" variant="outlined">Actualizar</Button>
      </Box>

      {/* Zonas: conteo y filtro de un toque */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        {ZONE_COLUMNS.map((z) => (
          <Chip
            key={z}
            label={`${z} · ${porZona[z] || 0}`}
            onClick={() => setZona(zona === z ? '' : z)}
            variant={zona === z ? 'filled' : 'outlined'}
            sx={{
              fontWeight: zona === z ? 700 : 400,
              bgcolor: zona === z ? ZONE_COLORS[z] : 'transparent',
              color: zona === z ? '#fff' : 'inherit',
              borderColor: ZONE_COLORS[z],
            }}
          />
        ))}
      </Stack>

      {/* Filtros */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            placeholder="Buscar nombre, teléfono, pueblo…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              // 16px evita que Safari haga zoom al enfocar en el teléfono
              style: { fontSize: 16 },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Etapa</InputLabel>
            <Select value={etapa} label="Etapa" onChange={(e) => setEtapa(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {Object.keys(STAGE_LABELS).map((k) => (
                <MenuItem key={k} value={k}>{STAGE_LABELS[k].label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Interés</InputLabel>
            <Select value={interes} label="Interés" onChange={(e) => setInteres(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="alto">Alto</MenuItem>
              <MenuItem value="medio">Medio</MenuItem>
              <MenuItem value="bajo">Bajo</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Ordenar por</InputLabel>
            <Select value={orden} label="Ordenar por" onChange={(e) => setOrden(e.target.value as Orden)}>
              <MenuItem value="reciente">Más reciente</MenuItem>
              <MenuItem value="seguimiento">Seguimiento</MenuItem>
              <MenuItem value="interes">Interés</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Antigüedad</InputLabel>
            <Select value={dias} label="Antigüedad" onChange={(e) => setDias(Number(e.target.value))}>
              <MenuItem value={3}>Últimos 3 días</MenuItem>
              <MenuItem value={7}>Última semana</MenuItem>
              <MenuItem value={30}>Último mes</MenuItem>
              <MenuItem value={90}>3 meses</MenuItem>
              <MenuItem value={0}>Todo</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup size="small">
            <ToggleButton value="esperan" selected={soloEsperan} onChange={() => setSoloEsperan(!soloEsperan)} sx={{ fontSize: 11, px: 1 }}>
              Esperan
            </ToggleButton>
            <ToggleButton value="seguimiento" selected={soloSeguimiento} onChange={() => setSoloSeguimiento(!soloSeguimiento)} sx={{ fontSize: 11, px: 1 }}>
              Con seguimiento
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {filtrados.length} de {leads.length} leads
      </Typography>

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : filtrados.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No hay leads con esos filtros.</Typography>
        </Paper>
      ) : esMovil ? (
        <Box>{filtrados.map(renderTarjeta)}</Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Zona</TableCell>
                <TableCell>Etapa</TableCell>
                <TableCell>En qué quedó</TableCell>
                <TableCell>Datos</TableCell>
                <TableCell>Seguimiento</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{filtrados.map(renderFila)}</TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Agendar seguimiento. No manda nada al cliente: a la hora, el recordatorio llega
          por Slack para que el mensaje lo escriba una persona. */}
      <Dialog open={!!agendando} onClose={() => setAgendando(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Seguimiento a {agendando ? leadName(agendando) : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ese día llega el recordatorio por Slack con el enlace al chat. No se le manda nada
            automático: el mensaje lo escribes tú.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" onClick={() => enDias(1)}>Mañana</Button>
            <Button size="small" variant="outlined" onClick={() => enDias(3)}>En 3 días</Button>
            <Button size="small" variant="outlined" onClick={() => enDias(7)}>En una semana</Button>
          </Stack>
          <TextField
            label="Cuándo" type="datetime-local" fullWidth sx={{ mb: 2 }}
            value={cuando} onChange={(e) => setCuando(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Qué hay que hacer" fullWidth multiline rows={2}
            value={nota} onChange={(e) => setNota(e.target.value)}
            placeholder="Confirmar la zona para coordinar la instalación"
          />
        </DialogContent>
        <DialogActions>
          {agendando?.pendingFollowUpAt && (
            <Button color="error" onClick={quitarAgenda} sx={{ mr: 'auto' }}>Quitar</Button>
          )}
          <Button onClick={() => setAgendando(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarAgenda}>Agendar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!aviso}
        autoHideDuration={3000}
        onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={aviso?.tipo || 'success'} onClose={() => setAviso(null)}>{aviso?.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadsBoardPage;
